import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { Link } from 'react-router-dom'
import { SEO, StructuredData } from '@/components/seo'

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-surface-950 text-white">
      <SEO
        title="About Us - Our Mission & Manifesto"
        description="Hiring breaks when teams stop trusting their signal. JumpstoneHR exists to fix that. Learn about our mission to transform hiring from a workflow problem into a decision system."
        canonical="/about"
        structuredData={[
          StructuredData.webPage(
            'About JumpstoneHR',
            'Learn about our mission to fix hiring. JumpstoneHR is building the infrastructure for evidence-based hiring decisions.',
            '/about'
          ),
          StructuredData.breadcrumb([
            { name: 'Home', url: '/' },
            { name: 'About', url: '/about' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About JumpstoneHR',
            description: 'Our manifesto on fixing hiring decisions with AI-powered evidence-based evaluation.',
            mainEntity: {
              '@type': 'Organization',
              name: 'Jumpstone Technology Inc.',
              foundingDate: '2024',
              founders: [{
                '@type': 'Organization',
                name: 'Nanthi Ventures',
                url: 'https://www.nanthiventures.com',
              }],
              knowsAbout: [
                'Artificial Intelligence',
                'Human Resources Technology',
                'Talent Acquisition',
                'Hiring Decision Systems',
              ],
            },
          },
        ]}
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-[0.03]">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
          
          {/* Gradient orb */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-brand-500/20 via-brand-600/10 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          
          <div className="relative mx-auto max-w-4xl section-padding">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 max-w-[60px] bg-brand-500" />
              <span className="text-brand-400 text-sm font-medium tracking-widest uppercase">Manifesto</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-8">
              Hiring breaks when teams stop trusting their{' '}
              <span className="text-brand-400">signal</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-surface-300 font-light max-w-2xl">
              Jumpstone HR exists to fix that.
            </p>
          </div>
        </section>

        {/* Core Belief Section */}
        <section className="py-24 bg-surface-950 relative overflow-hidden">
          {/* Accent line */}
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-500 via-brand-600 to-transparent" />
          
          <div className="mx-auto max-w-4xl section-padding">
            
            <div className="space-y-12">
              <div className="text-3xl sm:text-4xl font-bold leading-tight text-white">
                Hiring is not a workflow problem.
                <br />
                <span className="text-brand-400">It's a decision problem.</span>
              </div>
              
              <div className="bg-surface-900 border border-surface-800 rounded-2xl p-8 sm:p-10">
                <blockquote className="text-xl sm:text-2xl text-white font-light leading-relaxed">
                  Hiring is a high-stakes decision made under uncertainty, using incomplete and messy evidence.
                </blockquote>
              </div>
              
              <p className="text-lg text-surface-300 leading-relaxed max-w-2xl">
                The job of a hiring system is not to eliminate uncertainty — it's to make that uncertainty <span className="text-white font-medium">visible</span>, <span className="text-white font-medium">comparable</span>, and <span className="text-white font-medium">manageable</span>.
              </p>
            </div>
          </div>
        </section>

        {/* What We're Building Section */}
        <section className="py-24 bg-surface-900">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mt-4 text-white">
                A hiring decision system
              </h2>
            </div>
            
            <p className="text-lg text-surface-300 leading-relaxed mb-12">
              Designed around how judgment actually forms:
            </p>
            
            <div className="space-y-6">
              {[
                { num: '01', text: 'Evidence comes in many forms: resumes, conversations, interviews, work samples.' },
                { num: '02', text: 'That evidence needs to be normalized before it can be compared.' },
                { num: '03', text: 'Different people will have different opinions — and that\'s okay.' },
                { num: '04', text: 'What matters is that decisions are made against a clear, shared bar.' },
              ].map((item) => (
                <div key={item.num} className="flex gap-6 items-start group">
                  <span className="text-brand-500 font-mono text-sm mt-1 opacity-60 group-hover:opacity-100 transition-opacity">{item.num}</span>
                  <p className="text-lg text-surface-200 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The Framework Section */}
        <section className="py-24 bg-surface-950">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="mb-16">
              <span className="text-brand-400 text-sm font-semibold tracking-widest uppercase">The Framework</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-4 text-white">
                Universal, not prescriptive
              </h2>
              <p className="text-lg text-surface-400 mt-4">
                Every team hires differently — but the best hiring systems share common structure:
              </p>
            </div>
            
            <div className="grid gap-6">
              {[
                {
                  title: 'Company-wide principles',
                  description: 'Values and behaviors that matter everywhere, weighted by role and level.',
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                    </svg>
                  ),
                },
                {
                  title: 'Role-specific competencies',
                  description: 'The skills required to actually do the job.',
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
                    </svg>
                  ),
                },
                {
                  title: 'Level-indexed expectations',
                  description: 'Candidates are evaluated relative to the level they\'re being hired into — not in the abstract.',
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5" />
                    </svg>
                  ),
                },
                {
                  title: 'A decision gate',
                  description: 'A mechanism that ensures standards don\'t drift and decisions are made deliberately.',
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                    </svg>
                  ),
                },
              ].map((item) => (
                <div key={item.title} className="bg-surface-900 border border-surface-800 rounded-xl p-6 hover:border-brand-500/30 transition-colors group">
                  <div className="flex gap-5">
                    <div className="w-12 h-12 rounded-lg bg-brand-500/10 text-brand-400 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-500/20 transition-colors">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                      <p className="text-surface-400">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-12 bg-gradient-to-r from-brand-500/10 to-transparent border-l-2 border-brand-500 rounded-r-xl p-6">
              <p className="text-lg text-surface-200">
                This is how great organizations scale hiring without losing quality.
              </p>
            </div>
          </div>
        </section>

        {/* Where AI Fits Section */}
        <section className="py-24 bg-surface-900">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <span className="text-brand-400 text-sm font-semibold tracking-widest uppercase">Where AI Fits</span>
                <h2 className="text-3xl sm:text-4xl font-bold mt-4 text-white mb-8">
                  And where it doesn't
                </h2>
                
                <div className="bg-surface-800/50 border border-surface-700 rounded-xl p-6 mb-8">
                  <p className="text-xl text-white font-medium">
                    AI is not here to replace human judgment.
                  </p>
                </div>
                
                <p className="text-lg text-surface-300 mb-6">It's here to:</p>
                
                <ul className="space-y-4">
                  {[
                    'extract signal from unstructured evidence',
                    'apply consistent standards',
                    'surface risks and uncertainty',
                    'reduce noise and bias introduced by inconsistency',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-brand-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      <span className="text-surface-200">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="space-y-6">
                <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span className="text-surface-400 text-sm font-medium">Every recommendation is attributable</span>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span className="text-surface-400 text-sm font-medium">Every decision has a record</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span className="text-surface-400 text-sm font-medium">Every human stays accountable</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why This Matters Now Section */}
        <section className="py-24 bg-surface-950">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="mb-16">
              <span className="text-brand-400 text-sm font-semibold tracking-widest uppercase">Why This Matters Now</span>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 mb-16">
              <div>
                <h3 className="text-xl font-semibold text-white mb-6">As companies grow:</h3>
                <ul className="space-y-3">
                  {[
                    'more people interview',
                    'standards drift',
                    'decisions get political',
                    'hiring slows or becomes arbitrary',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 5.25l-7.5 7.5-7.5-7.5m15 6l-7.5 7.5-7.5-7.5" />
                      </svg>
                      <span className="text-surface-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-6">At the same time, AI has made it possible to:</h3>
                <ul className="space-y-3">
                  {[
                    'normalize messy evidence',
                    'compare candidates more consistently',
                    'scale judgment without scaling chaos',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <svg className="w-4 h-4 text-brand-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
                      </svg>
                      <span className="text-surface-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>            
          </div>
        </section>

        {/* Incubated By Section */}
        <section className="py-24 bg-surface-950">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="grid lg:grid-cols-[1fr_2fr] gap-12 items-start">
              <div>
                <span className="text-brand-400 text-sm font-semibold tracking-widest uppercase">Incubated By</span>
                <h2 className="text-2xl font-bold mt-3 text-white">Nanthi Ventures</h2>
              </div>
              
              <div className="space-y-6">
                <p className="text-lg text-surface-300 leading-relaxed">
                  JumpstoneHR is incubated by{' '}
                  <a href="https://www.nanthiventures.com" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-300 transition-colors">
                    Nanthi Ventures
                  </a>
                  , a venture studio founded by operators with experience scaling engineering teams at Y Combinator companies, Amazon, and Meta.
                </p>
                
                <p className="text-lg text-surface-300 leading-relaxed">
                  We've seen firsthand how hiring breaks at scale — and we're building the tools we wish we'd had.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Closing CTA Section */}
        <section className="py-32 bg-surface-950 relative overflow-hidden">
          <div className="relative mx-auto max-w-4xl section-padding text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
              <Link to="/" className="btn bg-brand-600 text-white hover:bg-brand-500 text-base px-8 py-4">
                Get Started
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  )
}

